import { AnimatePresence, motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { useBrand } from '@/shared/lib/brand';
import { cn } from '@/shared/lib/cn';

interface PublicInviteSubmittingOverlayProps {
  /** true depois que a chamada de cadastro já respondeu com sucesso (breve "check" antes de trocar de tela). */
  succeeded: boolean;
}

/**
 * Morfismo logo → check durante o envio do cadastro — mesma linguagem visual
 * de `features/operator/ui/operator-success-overlay.tsx` (pontuação do
 * operador), simplificado (sem confete/recompensa, que não fazem sentido
 * aqui). Fica visível enquanto `PublicInviteFormPanel` estiver com
 * `submitting=true` (substitui o form por inteiro nesse momento).
 */
export function PublicInviteSubmittingOverlay({ succeeded }: PublicInviteSubmittingOverlayProps) {
  const { brand } = useBrand();

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-16 text-center">
      <div className="relative grid size-28 place-items-center sm:size-32">
        <AnimatePresence mode="wait">
          {!succeeded ? (
            <motion.div
              key="logo"
              className="absolute inset-0 grid place-items-center"
              initial={{ opacity: 0, scale: 0.7, rotate: -8 }}
              animate={{ opacity: 1, scale: [1, 1.06, 1], rotate: 0 }}
              exit={{ opacity: 0, scale: 0.55, rotate: 12, filter: 'blur(4px)' }}
              transition={{
                opacity: { duration: 0.4 },
                rotate: { duration: 0.4 },
                scale: { duration: 1.6, repeat: Infinity, ease: 'easeInOut' },
              }}
            >
              <img
                src={brand.assets.icon.brand}
                alt=""
                width={88}
                height={88}
                decoding="async"
                className="size-20 object-contain drop-shadow-[0_8px_24px_rgba(255,192,1,0.4)] dark:drop-shadow-[0_0_28px_rgba(255,192,1,0.5)] sm:size-[5.5rem]"
              />
            </motion.div>
          ) : (
            <motion.div
              key="check"
              className="absolute inset-0 grid place-items-center"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: 'spring', stiffness: 380, damping: 22 }}
            >
              <span
                className={cn(
                  'grid size-20 place-items-center rounded-full bg-primary text-primary-foreground shadow-glass sm:size-[5.5rem]',
                  'ring-4 ring-primary/25',
                )}
              >
                <Check className="size-10 sm:size-12" strokeWidth={2.75} />
              </span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      <motion.p
        className="text-base font-medium text-muted-foreground"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {succeeded ? 'Cadastro confirmado!' : 'Enviando seu cadastro...'}
      </motion.p>
    </div>
  );
}
